import { useCallback, useEffect, useState } from "react";
/**
 * Loading a chunk on first use, and drawing whatever state that is in.
 *
 * Two transcript surfaces reach a heavy library through a dynamic `import()` —
 * the mermaid renderer and the process-flow canvas — and both need the same two
 * things: one download shared by every consumer in the session, and a failure
 * that can be retried instead of poisoning every later use with the same
 * rejected promise.
 */ /**
 * Memoises one dynamic import, and forgets it if it fails.
 *
 * Without the memo a transcript with three diagrams asks three times; without
 * the self-clearing `catch` a chunk that failed once (a deploy mid-session, a
 * dropped connection) hands the same rejection to every later caller, so the
 * retry below could never succeed.
 */ export function createLazyModuleLoader(load) {
    let inFlight = null;
    return function loadOnce() {
        if (inFlight) return inFlight;
        const attempt = load();
        void attempt.catch(()=>{
            if (inFlight === attempt) inFlight = null;
        });
        inFlight = attempt;
        return attempt;
    };
}
/**
 * The chunk behind a `createLazyModuleLoader`, as render state.
 *
 * `label` names the chunk in the console: a chunk that silently fails to arrive
 * looks exactly like one that is still arriving, so the error is logged rather
 * than swallowed.
 */ export function useLazyModule(loadOnce, label) {
    const [attempt, setAttempt] = useState(0);
    const [state, setState] = useState({
        status: "pending"
    });
    // Both writes belong to the click, not to the effect that follows it: resetting
    // to `pending` inside the effect would make every FIRST mount render twice for
    // a state it already starts in.
    const retry = useCallback(()=>{
        setState({
            status: "pending"
        });
        setAttempt((previous)=>previous + 1);
    }, []);
    // Downloading a chunk is external synchronization, not derivable state.
    useEffect(function loadModule() {
        let superseded = false;
        loadOnce().then((module)=>{
            if (!superseded) setState({
                status: "ready",
                module
            });
        }, (error)=>{
            console.error(`${label} failed to load:`, error);
            if (!superseded) setState({
                status: "failed",
                retry
            });
        });
        return ()=>{
            superseded = true;
        };
    }, [
        loadOnce,
        label,
        attempt,
        retry
    ]);
    return state;
}

//# sourceMappingURL=lazy-module.js.map